import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  FiTarget, FiEye, FiHeart, FiAward, FiShield, 
  FiTrendingUp, FiUsers, FiShoppingBag, FiCheckCircle, 
  FiTruck, FiFileText 
} from 'react-icons/fi';

const About = () => {
  const coreValues = [
    {
      icon: FiHeart,
      title: 'Customer First',
      description: 'Your success is our priority. We go above and beyond to meet your needs.',
    },
    {
      icon: FiAward,
      title: 'Quality Assurance',
      description: 'Only the best products delivered. We verify every item before dispatch.',
    },
    {
      icon: FiShield,
      title: 'Transparency',
      description: 'Clear pricing, honest communication, and reliable service every time.',
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation',
      description: 'Modern procurement solutions that adapt to your evolving business needs.',
    },
  ];

  const operatingProcess = [
    {
      icon: FiUsers,
      title: 'Client Acquisition & Tender Management',
      description: 'We actively engage with clients and participate in tender bidding processes. Our experienced team understands institutional procurement requirements and ensures compliance with all specifications.',
      points: [
        'Proactive client engagement',
        'Competitive tender bidding',
        'Compliance with specifications',
        'Professional proposals',
      ],
    },
    {
      icon: FiShoppingBag,
      title: 'Dynamic Sourcing & Procurement',
      description: 'Our stock-less model means we source products dynamically from our extensive network of vetted suppliers. This allows us to offer competitive prices and unlimited product variety.',
      points: [
        'Vetted supplier database',
        'Best price negotiations',
        'Wide product variety',
        'Quality supplier relationships',
      ],
    },
    {
      icon: FiCheckCircle,
      title: 'Quality Assurance',
      description: 'Every product goes through rigorous inspection and verification. We consolidate orders from multiple sources to ensure you receive exactly what you ordered.',
      points: [
        'Product inspection',
        'Quality verification',
        'Order consolidation',
        'Standards compliance',
      ],
    },
    {
      icon: FiTruck,
      title: 'Logistics & Delivery',
      description: 'We handle all logistics to ensure timely delivery to your location. Our tracking system provides transparency, and we provide proof of delivery for every order.',
      points: [
        'Timely transport',
        'Order tracking',
        'Proof of delivery',
        'Nationwide coverage',
      ],
    },
    {
      icon: FiFileText,
      title: 'Professional Client Management',
      description: 'From quotation to final invoice, we maintain clear documentation and provide dedicated customer support throughout your procurement journey.',
      points: [
        'Clear quotations',
        'Detailed invoices',
        'Dedicated support',
        'Account management',
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>About Us - NeoVam General Supplies</title>
        <meta name="description" content="Learn about NeoVam General Supplies: your trusted partner in business essentials. Discover our mission, vision, values, and unique operating model." />
      </Helmet>

      <div className="min-h-screen pt-[120px]">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-fade-in">
              About <span className="text-gradient">NeoVam General Supplies</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto animate-slide-up">
              Your trusted partner in business essentials, committed to simplifying procurement for businesses and institutions across Tanzania.
            </p>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-in-left">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  Who <span className="text-gradient">We Are</span>
                </h2>
                <div className="space-y-4 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  <p>
                    <strong className="text-gray-900 dark:text-white">NeoVam General Supplies</strong> is a dynamic procurement hub based in Dar es Salaam, Tanzania. We specialize in being the one-stop supplier of comprehensive business and institutional supplies.
                  </p>
                  <p>
                    Our unique <strong className="text-gray-900 dark:text-white">stock-less operating model</strong> sets us apart. Rather than maintaining costly inventory, we leverage an extensive network of vetted suppliers to source products on demand. This means unlimited variety, competitive pricing, and the agility to fulfill any request.
                  </p>
                  <p>
                    Whether you need office stationery, ICT equipment, cleaning supplies, construction materials, or specialized procurement, we handle it all with professionalism and efficiency.
                  </p>
                </div>
              </div>
              <div className="animate-slide-in-right">
                <div className="card bg-gradient-to-br from-primary-50 to-primary-100 dark:from-dark-800 dark:to-dark-900 border-none">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Location
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        P.O BOX 36098, Kigamboni, Dar es Salaam, Tanzania
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Core Business
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        One-stop supplier of comprehensive business and institutional supplies
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Operating Model
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Stock-less, dynamic procurement hub
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Contact
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        info@neovam.com
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-12 sm:py-16 md:py-20 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="card hover:scale-105 animate-fade-in">
                <FiTarget className="w-16 h-16 text-primary-400 mb-4" />
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Our Mission
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  To empower businesses and institutions by simplifying their procurement process through a single, dependable source for all supply needs.
                </p>
              </div>
              <div className="card hover:scale-105 animate-fade-in animate-delay-200">
                <FiEye className="w-16 h-16 text-primary-400 mb-4" />
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Our Vision
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  To be the most responsive and reliable general supplies partner for every company and institution in Tanzania.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Our <span className="text-gradient">Core Values</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                The principles that guide everything we do
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  className="card text-center hover:scale-105 animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <value.icon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How We Operate */}
        <section className="py-12 sm:py-16 md:py-20 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                How <span className="text-gradient">We Operate</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Our comprehensive procurement process ensures quality, transparency, and efficiency at every step
              </p>
            </div>
            <div className="space-y-8">
              {operatingProcess.map((process, index) => (
                <div
                  key={index}
                  className="card animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="grid lg:grid-cols-3 gap-6">
                    <div>
                      <process.icon className="w-12 h-12 text-primary-400 mb-4" />
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {process.title}
                      </h3>
                    </div>
                    <div className="lg:col-span-2 space-y-4">
                      <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                        {process.description}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {process.points.map((point, pointIndex) => (
                          <div key={pointIndex} className="flex items-start gap-2">
                            <FiCheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 dark:text-gray-300">{point}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Promise */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="card bg-gradient-to-br from-primary-50 to-primary-100 dark:from-dark-800 dark:to-dark-900 border-none animate-fade-in">
              <FiShield className="w-20 h-20 text-primary-400 mx-auto mb-6" />
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our <span className="text-gradient">Promise</span>
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
                "We don't just deliver products; we deliver peace of mind. When you partner with NeoVam General Supplies, you gain a dedicated procurement team committed to your success."
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-primary text-white text-lg font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300"
              >
                Partner With Us
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
